import { useCallback, useRef, useState } from "react"

interface PendingDraftSend {
  id: number
  snapshot: string
  accepted: boolean
}

interface UseCopilotDraftResult {
  draft: string
  setDraft: (value: string) => void
  isSendPending: boolean
  beginSend: (snapshot: string) => number | null
  acceptSend: (requestId: number) => void
  failSend: (requestId: number) => void
  completeSend: (requestId: number) => void
}

export const useCopilotDraft = (): UseCopilotDraftResult => {
  const [draft, setDraft] = useState("")
  const [isSendPending, setIsSendPending] = useState(false)
  const nextRequestIdRef = useRef(0)
  const pendingSendRef = useRef<PendingDraftSend | null>(null)

  const beginSend = useCallback((snapshot: string): number | null => {
    if (pendingSendRef.current !== null) return null

    const id = nextRequestIdRef.current + 1
    nextRequestIdRef.current = id
    pendingSendRef.current = { id, snapshot, accepted: false }
    setIsSendPending(true)
    setDraft((current) => (current === snapshot ? "" : current))
    return id
  }, [])

  const acceptSend = useCallback((requestId: number) => {
    const pending = pendingSendRef.current
    if (!pending || pending.id !== requestId || pending.accepted) return

    pending.accepted = true
  }, [])

  const finishSend = useCallback((requestId: number) => {
    const pending = pendingSendRef.current
    if (!pending || pending.id !== requestId) return

    if (!pending.accepted) {
      setDraft((current) => (current.length > 0 ? current : pending.snapshot))
    }

    pendingSendRef.current = null
    setIsSendPending(false)
  }, [])

  return {
    draft,
    setDraft,
    isSendPending,
    beginSend,
    acceptSend,
    failSend: finishSend,
    completeSend: finishSend,
  }
}
